import * as Sentry from '@sentry/react';

const SENSITIVE_PARAMS = ['authUrl', 'access_token', 'id_token', 'refresh_token', 'token'];

/**
 * Redacts sensitive query params and hash fragment values from a URL string.
 * Handles both the top-level URL and any nested URLs encoded in query params
 * (e.g. /success?authUrl=<encoded callback with #access_token=...>).
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') return url;

  try {
    // Parse relative URLs by using a placeholder origin.
    const hasOrigin = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
    const parsed = new URL(url, hasOrigin ? undefined : 'http://placeholder.invalid');

    parsed.searchParams.forEach((value, key) => {
      if (SENSITIVE_PARAMS.includes(key)) {
        parsed.searchParams.set(key, '[Filtered]');
      } else if (/^https?:\/\//i.test(value) || value.includes('access_token=')) {
        // Param value may itself be a URL containing sensitive data.
        parsed.searchParams.set(key, sanitizeUrl(value));
      }
    });

    if (parsed.hash) {
      const hashBody = parsed.hash.startsWith('#') ? parsed.hash.slice(1) : parsed.hash;
      const hashParams = new URLSearchParams(hashBody);
      let hashChanged = false;
      hashParams.forEach((_value, key) => {
        if (SENSITIVE_PARAMS.includes(key)) {
          hashParams.set(key, '[Filtered]');
          hashChanged = true;
        }
      });
      if (hashChanged) {
        parsed.hash = `#${hashParams.toString()}`;
      }
    }

    const result = parsed.toString();
    return hasOrigin ? result : result.replace('http://placeholder.invalid', '');
  } catch {
    return url;
  }
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development';
  const sentryEnabledFlag = String(process.env.REACT_APP_SENTRY_ENABLED).toLowerCase() === 'true';
  const enabled = sentryEnabledFlag && process.env.NODE_ENV !== 'test';

  if (!dsn || !enabled) return;

  Sentry.init({
    dsn,
    environment,
    enabled,
    beforeSend(event) {
      if (event.request?.url) {
        event.request.url = sanitizeUrl(event.request.url);
      }
      if (event.request?.headers?.Referer) {
        event.request.headers.Referer = sanitizeUrl(event.request.headers.Referer);
      }
      if (event.tags) {
        for (const key of Object.keys(event.tags)) {
          if (typeof event.tags[key] === 'string' && event.tags[key].includes('://')) {
            event.tags[key] = sanitizeUrl(event.tags[key]);
          }
        }
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = sanitizeUrl(breadcrumb.data.url);
      }
      if (breadcrumb.data?.to) {
        breadcrumb.data.to = sanitizeUrl(breadcrumb.data.to);
      }
      if (breadcrumb.data?.from) {
        breadcrumb.data.from = sanitizeUrl(breadcrumb.data.from);
      }
      return breadcrumb;
    },
  });
}

export { Sentry };
